import { DOCUMENT, Injectable, PLATFORM_ID, inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { Meta, Title } from '@angular/platform-browser';
import { ENVIRONMENT } from '@vendorhub/shared/config';

/**
 * Shape accepted by `SeoService.update()`. All fields optional — pass only what
 * changes between routes. Arrays of strings (e.g. `keywords`) are joined with
 * commas; `ogImage` / `canonical` are auto-absolutized against `appBaseUrl`.
 */
export interface SeoTags {
  title?: string;
  description?: string;
  keywords?: string[];
  canonical?: string;
  robots?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogImage?: string;
  ogType?: 'website' | 'article' | 'product';
  twitterCard?: 'summary' | 'summary_large_image';
  locale?: string;
}

/**
 * Emits a single `application/ld+json` <script> tag for the given structured
 * data object. Replaces the previous JSON-LD block if one exists so consecutive
 * route changes don't stack up.
 */
export interface JsonLdPayload {
  readonly '@context': 'https://schema.org' | string;
  readonly '@type': string;
  readonly [key: string]: unknown;
}

const JSON_LD_ID = 'vh-jsonld';

@Injectable({ providedIn: 'root' })
export class SeoService {
  private readonly title = inject(Title);
  private readonly meta = inject(Meta);
  private readonly env = inject(ENVIRONMENT);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly doc = inject(DOCUMENT);

  /**
   * Apply a set of SEO tags. Safe to call during SSR — the service writes
   * through Angular's Meta/Title APIs which are platform-agnostic, and falls
   * back to document manipulation for <link rel="canonical"> / JSON-LD.
   */
  update(tags: SeoTags): void {
    if (tags.title) {
      this.title.setTitle(tags.title);
    }

    this.upsertName('description', tags.description);
    this.upsertName(
      'keywords',
      tags.keywords && tags.keywords.length ? tags.keywords.join(', ') : undefined,
    );
    this.upsertName('robots', tags.robots);

    // Open Graph
    this.upsertProperty('og:title', tags.ogTitle ?? tags.title);
    this.upsertProperty('og:description', tags.ogDescription ?? tags.description);
    this.upsertProperty('og:type', tags.ogType ?? 'website');
    this.upsertProperty('og:image', tags.ogImage ? this.absolutize(tags.ogImage) : undefined);
    this.upsertProperty('og:locale', tags.locale);

    // Twitter
    this.upsertName('twitter:card', tags.twitterCard ?? 'summary_large_image');
    this.upsertName('twitter:title', tags.ogTitle ?? tags.title);
    this.upsertName('twitter:description', tags.ogDescription ?? tags.description);
    this.upsertName('twitter:image', tags.ogImage ? this.absolutize(tags.ogImage) : undefined);

    if (tags.canonical) {
      this.setCanonical(this.absolutize(tags.canonical));
    }
  }

  /**
   * Writes (or replaces) a JSON-LD <script> element in <head>. Passing
   * `undefined` removes any existing block.
   */
  setJsonLd(payload: JsonLdPayload | undefined): void {
    const head = this.doc.head;
    if (!head) return;

    const existing = this.doc.getElementById(JSON_LD_ID);
    if (existing) existing.remove();

    if (!payload) return;

    const script = this.doc.createElement('script');
    script.setAttribute('type', 'application/ld+json');
    script.id = JSON_LD_ID;
    script.textContent = JSON.stringify(payload);
    head.appendChild(script);
  }

  /**
   * Remove per-route tags on navigation away. Keeps the default site title.
   */
  reset(fallbackTitle = 'VendorHub'): void {
    this.title.setTitle(fallbackTitle);
    [
      'description',
      'keywords',
      'robots',
      'twitter:card',
      'twitter:title',
      'twitter:description',
      'twitter:image',
    ].forEach((name) => this.meta.removeTag(`name='${name}'`));
    ['og:title', 'og:description', 'og:type', 'og:image', 'og:locale'].forEach((prop) =>
      this.meta.removeTag(`property='${prop}'`),
    );
    this.setCanonical(null);
    this.setJsonLd(undefined);
  }

  private upsertName(name: string, content: string | undefined): void {
    if (content === undefined || content === null || content === '') {
      this.meta.removeTag(`name='${name}'`);
      return;
    }
    this.meta.updateTag({ name, content }, `name='${name}'`);
  }

  private upsertProperty(property: string, content: string | undefined): void {
    if (content === undefined || content === null || content === '') {
      this.meta.removeTag(`property='${property}'`);
      return;
    }
    this.meta.updateTag({ property, content }, `property='${property}'`);
  }

  private setCanonical(href: string | null): void {
    const head = this.doc.head;
    if (!head) return;
    let link = head.querySelector<HTMLLinkElement>("link[rel='canonical']");
    if (!href) {
      link?.remove();
      return;
    }
    if (!link) {
      link = this.doc.createElement('link');
      link.setAttribute('rel', 'canonical');
      head.appendChild(link);
    }
    link.setAttribute('href', href);
  }

  /**
   * Resolves relative paths against `appBaseUrl`. Absolute URLs pass through.
   * During SSR we may not have a document origin; fall back to env config.
   */
  private absolutize(path: string): string {
    if (/^https?:\/\//i.test(path)) return path;
    const base = this.env.appBaseUrl?.replace(/\/$/, '') ?? '';
    if (isPlatformBrowser(this.platformId) && !base) {
      return new URL(path, this.doc.location.href).toString();
    }
    return `${base}${path.startsWith('/') ? '' : '/'}${path}`;
  }
}
